'use client';

import { useEffect, useRef, useState } from 'react';
import { CalendarMark, MonthGrid, dayLabel, dayOf, isoDay, monthOf } from './month-grid';

/**
 * One control for "which window of time", the shape Artur asked for after the
 * affiliate panel's: a button saying the window, a list of the ways people
 * actually name one, and two date fields for the case none of them covers.
 *
 * It replaced two bare `<input type="date">` and a row of quick chips. The
 * chips answered "last 7 days" in one click and nothing else; the inputs
 * answered anything and took six. Neither could say what it was showing —
 * this says it on its own face, which is what makes a filter you can trust
 * without opening it.
 *
 * Both ends are `YYYY-MM-DD`, inclusive, and empty means unbounded. Local
 * dates on purpose: "today" is the reader's today, and a UTC day boundary
 * would hide this morning's cases for anybody west of London.
 */
export interface DateWindow {
  from: string;
  to: string;
}

const iso = isoDay;

function daysAgo(days: number): Date {
  const at = new Date();
  at.setDate(at.getDate() - days);
  return at;
}

/** Monday, because a support week starts on one. */
function weekStart(): Date {
  const at = new Date();
  const back = (at.getDay() + 6) % 7;
  at.setDate(at.getDate() - back);
  return at;
}

function monthStart(offset = 0): Date {
  const at = new Date();
  return new Date(at.getFullYear(), at.getMonth() + offset, 1);
}

function monthEnd(offset = 0): Date {
  const at = new Date();
  return new Date(at.getFullYear(), at.getMonth() + offset + 1, 0);
}

const PRESETS: ReadonlyArray<{ key: string; label: string; window: () => DateWindow }> = [
  { key: 'today', label: 'Today', window: () => ({ from: iso(new Date()), to: iso(new Date()) }) },
  { key: 'yesterday', label: 'Yesterday', window: () => ({ from: iso(daysAgo(1)), to: iso(daysAgo(1)) }) },
  { key: 'week', label: 'This week', window: () => ({ from: iso(weekStart()), to: iso(new Date()) }) },
  { key: 'last7', label: 'Last 7 days', window: () => ({ from: iso(daysAgo(6)), to: iso(new Date()) }) },
  { key: 'month', label: 'This month', window: () => ({ from: iso(monthStart()), to: iso(new Date()) }) },
  { key: 'last30', label: 'Last 30 days', window: () => ({ from: iso(daysAgo(29)), to: iso(new Date()) }) },
  { key: 'lastMonth', label: 'Last month', window: () => ({ from: iso(monthStart(-1)), to: iso(monthEnd(-1)) }) },
  { key: 'all', label: 'All time', window: () => ({ from: '', to: '' }) },
];

/** Which preset a window is, if it is one — so reopening the list shows the
 *  reader their own choice rather than nothing. */
function presetOf(window: DateWindow): string {
  const match = PRESETS.find((preset) => {
    const its = preset.window();
    return its.from === window.from && its.to === window.to;
  });
  return match?.key ?? 'custom';
}

/* What the button says. A range reads as its two ends; a preset reads as its
   own name, because "Last 30 days" is the fact and the two dates are its
   arithmetic. Unfiltered reads as the *question*, not as "All time": in a row
   of controls with no labels over them, every one of them has to say which
   question it answers (Artur, 2026-09-18). */
function faceOf(window: DateWindow, label: string): string {
  const key = presetOf(window);
  if (key === 'all') return `${label} any time`;
  if (key !== 'custom') return PRESETS.find((preset) => preset.key === key)!.label;
  if (window.from && window.to) {
    return window.from === window.to ? dayLabel(window.from) : `${dayLabel(window.from)} – ${dayLabel(window.to)}`;
  }
  if (window.from) return `since ${dayLabel(window.from)}`;
  return `until ${dayLabel(window.to)}`;
}

/** "17 Sep – 23 Sep · 7 days", under the calendar while a range is drafted. */
function spanOf(window: DateWindow): string {
  const from = dayOf(window.from);
  const to = dayOf(window.to);
  if (!from && !to) return 'Any time';
  if (!from || !to) return window.from ? `From ${dayLabel(window.from)}` : `Until ${dayLabel(window.to)}`;
  const days = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
  const ends = window.from === window.to ? dayLabel(window.from) : `${dayLabel(window.from)} – ${dayLabel(window.to)}`;
  return `${ends} · ${days} ${days === 1 ? 'day' : 'days'}`;
}

/**
 * Presets one click away on the left, a month on the right for the odd range,
 * the draft in words above Apply. First click sets the start, the next the
 * end; picked backwards, the earlier day becomes the start.
 */
export function DateRangeField({
  value,
  onChange,
  label = 'Opened',
}: {
  value: DateWindow;
  onChange: (next: DateWindow) => void;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateWindow>(value);
  const [month, setMonth] = useState<Date>(() => monthOf(value.to || value.from));
  const box = useRef<HTMLDivElement>(null);

  // The month is a draft until Apply: picking the first end must not already
  // filter the list, or every range would flash through a one-day filter.
  // Keyed on the two dates, not on the object: a page that rebuilds its
  // window on every render handed a new one each time, and the draft was
  // reset between the first click and the second.
  useEffect(() => {
    if (!open) return;
    setDraft({ from: value.from, to: value.to });
    setMonth(monthOf(value.to || value.from));
  }, [open, value.from, value.to]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const today = isoDay(new Date());

  function pick(day: string) {
    setDraft((was) => {
      // Nothing drafted, or a whole range already: this click starts a new one.
      if (!was.from || was.to) return { from: day, to: '' };
      return day < was.from ? { from: day, to: was.from } : { from: was.from, to: day };
    });
  }

  // A single click is a one-day range once applied: "from" alone would read
  // as "since", which is not what a click on one day meant.
  const applied: DateWindow = draft.from && !draft.to ? { from: draft.from, to: draft.from } : draft;

  return (
    <div className="dr" ref={box}>
      <button
        type="button"
        className="dr-trigger field field-sm"
        aria-expanded={open}
        aria-label={`${label}: ${faceOf(value, label)}`}
        onClick={() => setOpen((was) => !was)}
      >
        <CalendarMark />
        <span className="dr-face">{faceOf(value, label)}</span>
        <span aria-hidden className="dr-caret">
          {'▾'}
        </span>
      </button>

      {open && (
        <div className="dr-pop" role="dialog" aria-label={`Choose ${label.toLowerCase()} window`}>
          <div className="dr-body">
            <div className="dr-presets">
              {PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  className={`dr-preset${presetOf(applied) === preset.key ? ' is-on' : ''}`}
                  onClick={() => {
                    onChange(preset.window());
                    setOpen(false);
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="dr-month">
              <MonthGrid
                month={month}
                onMonth={setMonth}
                from={draft.from || undefined}
                to={draft.to || draft.from || undefined}
                max={today}
                onPick={pick}
              />
            </div>
          </div>
          <div className="dr-foot">
            <span className="dr-span">{spanOf(applied)}</span>
            <button type="button" className="btn btn-sm" onClick={() => setDraft({ from: '', to: '' })}>
              {'Clear'}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => {
                onChange(applied);
                setOpen(false);
              }}
            >
              {'Apply'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
